import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExternalLink, Star, Globe, Film, Zap, BookOpen,
  Calendar, Clock, Award, Users, ChevronRight, X, Play
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// ─── BASE URL for all filmography images ─────────────────────────────────────
const BASE = "https://director-keshhav.skywork.website/images/";

function img(name: string): string {
  return BASE + encodeURIComponent(name);
}

interface PastProject {
  id: string;
  title: string;
  tagline: string;
  description: string;
  year: string;
  genre: string;
  duration: string;
  format?: string;
  budget?: string;
  awards: string;
  cast: string[];
  poster: string;
  galleryImages: string[];
  highlights: string[];
}

// ─── DATA ─────────────────────────────────────────────────────────────────────

const currentProduction = {
  id: "chaos",
  title: "CHAOS AFRA-TAFRI",
  tagline: "The App That Could Trigger World War III",
  description:
    "An AI-powered geopolitical thriller spanning 6 countries with 70+ minutes of cutting-edge VFX. A high-stakes narrative exploring technology's power to reshape global order.",
  status: "IN PRODUCTION",
  year: "2026",
  genre: "AI Geopolitical Thriller",
  duration: "120+ minutes",
  format: "Feature Film",
  poster: img("chaos_complete_storyline_poster_1.jpg"),
  badges: ["6 Countries", "70+ Min VFX", "2026", "Feature Film"],
  cast: ["Gulshan Grover", "Sunny Leone", "Arbaaz Khan", "Jagapathi Babu"],
  highlights: [
    "6 Countries Production",
    "70+ Minutes VFX Sequences",
    "AI-Driven Narrative",
    "International Star Cast",
    "Geopolitical Thriller Genre",
  ],
  officialWebsite: "https://director-keshhav.skywork.website",
};

const pastProjects: PastProject[] = [
  {
    id: "catfight",
    title: "CATFIGHT",
    tagline: "Love Beyond Labels",
    description:
      "India's groundbreaking LGBTQ+ drama that shattered conventions and won 109+ international awards. A powerful exploration of suppressed emotions and societal expectations.",
    year: "2016",
    genre: "LGBTQ+ Drama",
    duration: "40 minutes",
    format: "8K Resolution",
    awards: "109+ International Awards",
    cast: ["Sonal Sehgal", "Sasha Goradia"],
    poster: img("catfight_new_tagline_poster_1.jpg"),
    galleryImages: [
      img("467898770_10161884086919866_2473197727237720433_n.jpg"),
      img("cropped_image_10_1777039611226419647.jpg"),
      img("cropped_image_10_1777039612819539244.jpg"),
    ],
    highlights: [
      "Best LGBTQ Film — Multiple International Festivals",
      "Best Director — 3 Continents",
      "Best International Short — Cannes, Sundance & Berlin Recognition",
    ],
  },
  {
    id: "jeena",
    title: "JEENA ISI KA NAAM HAI",
    tagline: "Life Is What You Make It",
    description:
      "A $10 million epic production featuring the historic White House shoot. A global cinematic achievement showcasing Indian storytelling on the world stage.",
    year: "2017",
    genre: "Drama",
    duration: "Feature Length",
    budget: "$10 Million",
    awards: "Multiple International Awards",
    cast: ["Arbaaz Khan", "Himansh Kohli", "Ashutosh Rana", "Prem Chopra"],
    poster: img("Jeena Isi Ka Naam Hai Poster.jpg"),
    galleryImages: [],
    highlights: [
      "Historic White House Filming Permission",
      "Global Theatrical Release",
      "$10M Production Budget",
    ],
  },
  {
    id: "eternal-love",
    title: "ETERNAL LOVE",
    tagline: "India's First Real 3D Musical Short",
    description:
      "Pioneering stereoscopic 3D technology in Indian cinema. A groundbreaking musical experience that pushed technical boundaries.",
    year: "2014",
    genre: "Musical Romance",
    duration: "Short Film",
    format: "Real 3D Stereoscopic",
    awards: "",
    cast: [],
    poster: img("Eternal_love_poster.jpg"),
    galleryImages: [],
    highlights: [
      "India's First Real 3D Musical Short Film",
      "Stereoscopic 3D Technology Pioneer",
      "Technical Innovation Award Winner",
    ],
  },
  {
    id: "khuda",
    title: "KHUDA MEHERBAAN",
    tagline: "Faith, Hope, and Redemption",
    description:
      "A spiritual journey exploring themes of faith and human resilience, reaching audiences across cultural and geographic boundaries.",
    year: "2015",
    genre: "Spiritual Drama",
    duration: "Short Film",
    awards: "",
    cast: [],
    poster: "",
    galleryImages: [],
    highlights: [
      "International Festival Selection",
      "Cross-Cultural Storytelling",
    ],
  },
  {
    id: "wheels",
    title: "THE WHEELS",
    tagline: "Life Keeps Moving",
    description:
      "A metaphorical exploration of life's continuous journey — movement, change, and the relentless flow of time.",
    year: "2013",
    genre: "Drama",
    duration: "Short Film",
    awards: "",
    cast: [],
    poster: "",
    galleryImages: [],
    highlights: ["Metaphorical Storytelling", "Visual Poetry"],
  },
  {
    id: "early-works",
    title: "EARLY WORKS (2002–2008)",
    tagline: "The Foundation Years",
    description:
      "A collection of experimental and foundational works that established Keshhav's unique cinematic voice. Includes music videos, commercials, and short films that showcased early mastery of visual storytelling.",
    year: "2002–2008",
    genre: "Various",
    duration: "Various",
    awards: "",
    cast: [],
    poster: "",
    galleryImages: [],
    highlights: [
      "Music Video Direction",
      "Commercial Production",
      "Experimental Short Films",
    ],
  },
];

const upcomingProjects = [
  { id: "monster", title: "MONSTER", tagline: "The Beast Within", genre: "Psychological Horror", description: "A psychological horror exploring the darkness that lurks within humanity.", poster: img("the_monster_poster_1.jpg") },
  { id: "karma", title: "SEE NO EVIL / KARMA HITS BACK", tagline: "What Goes Around Comes Around", genre: "Supernatural Thriller", description: "A supernatural thriller examining the consequences of moral choices.", poster: img("see_no_evil_final_revenge_1.jpg") },
  { id: "vengeance", title: "ACT OF VENGEANCE", tagline: "Justice Has No Mercy", genre: "Action Thriller", description: "An action thriller about revenge and redemption.", poster: img("act_of_vengeance_cinematic_1.jpg") },
  { id: "wife", title: "I LOVE YOUR WIFE", tagline: "A Dangerous Obsession", genre: "Psychological Drama", description: "A psychological drama exploring forbidden attraction and age-appropriate complexity.", poster: img("i_love_your_wife_age_appropriate_drama_poster_1.jpg") },
  { id: "case333", title: "CASE #333", tagline: "The Unsolvable Mystery", genre: "Crime Thriller", description: "A crime thriller based on an enigmatic cold case.", poster: img("WhatsApp Image 2026-04-02 at 04.10.42.jpeg") },
  { id: "sirf", title: "SIRF TERE LIYE", tagline: "Only For You", genre: "Romantic Drama", description: "A romantic drama celebrating unconditional obsession and madness of love.", poster: img("sirf_tere_liye_obsession_madness_love_1.jpg") },
  { id: "duffers", title: "3 DUFFERS", tagline: "Three Misfits. One Hilarious Journey.", genre: "Comedy", description: "A cinematic comedy following three lovable misfits on an unforgettable adventure.", poster: img("3_duffers_cinematic_detailed_1.jpg") },
  { id: "tadka", title: "INDIAN TADKA", tagline: "A Night to Remember", genre: "Comedy Drama", description: "A high-stakes comedic drama unfolding over one unforgettable poker night.", poster: img("script_poster_poker_night_v1_1.jpg") },
  { id: "koi-mil-gaye", title: "KOI MIL GAYE", tagline: "When Destiny Speaks", genre: "Romantic Drama", description: "A romantic journey exploring the magic of unexpected love.", poster: img("koi_mil_gayi_poster_edited_1.jpg") },
  { id: "affoo", title: "AFFOO KHUDAYA", tagline: "Dark Humor. Divine Chaos.", genre: "Dark Comedy", description: "A darkly comic exploration of fate, faith, and the absurdity of everyday life.", poster: img("affoo_khudaya_dark_humor_1.jpg") },
  { id: "dimensions", title: "DIMENSIONS OF LOVE", tagline: "She was his universe. He was her home.", genre: "Romantic Epic", description: "A sweeping romantic epic starring Anika Chatterjee and Raj Patel. Written & Directed by Keshhav Panneriy. Music by Shankar-Ehsan-Loy.", poster: img("dimensions_v3_1.jpg") },
  { id: "body", title: "BODY SNATCHER", tagline: "When Divine Protectors Become Cosmic Destroyers", genre: "Cosmic Sci-Fi Thriller", description: "An epic cosmic thriller exploring ancient Anunaki mythology and the battle for consciousness across galaxies. The Fallen God of Minds.", poster: img("body_snatcher_character_poster_1.jpg") },
];

const epicUniverses = [
  {
    id: "pc-universe",
    title: "PC CINEMATIC UNIVERSE",
    tagline: "Six Volumes Spanning 1854–3030",
    description: "An unprecedented cinematic universe spanning 1,176 years of history and future. Six interconnected volumes creating India's first true cinematic universe with global ambitions.",
    year: "2027–2032",
    poster: img("pc_vikram_clean_poster_1_3273.jpg"),
    badges: ["6 Volumes", "Multi-Film Franchise", "2027–2032"],
    highlights: [
      "6-Volume Epic Spanning 1854–3030",
      "India's First True Cinematic Universe",
      "Multi-Generational Storytelling",
      "Global Production Scale",
      "Revolutionary Narrative Architecture",
    ],
  },
  {
    id: "supreme",
    title: "SUPREME CONSCIOUSNESS",
    tagline: "The Ultimate Spiritual Epic",
    description: "A six-volume spiritual epic with $70M budget, exploring consciousness, divinity, and human transcendence. Projected $1.2B worldwide gross.",
    year: "2029",
    poster: img("krishna_consciousness_epic_no_marvel_1_2968.jpg"),
    badges: ["6 Volumes", "$70 Million", "December 2029", "$1.2 Billion Worldwide"],
    highlights: [
      "$70M Production Budget",
      "$1.2B Worldwide Gross Estimate",
      "6-Volume Spiritual Journey",
      "December 2029 Release",
      "Global Distribution Strategy",
    ],
  },
  {
    id: "nawabistan",
    title: "NAWABISTAN — TALE OF TWO NAWABS",
    tagline: "Two Worlds. One Shore. Eternal Rivalry.",
    description: "An epic period drama exploring the rivalry and intertwined fates of two powerful Nawabs on the shores of an ancient kingdom. A sweeping saga of power, honor, and destiny.",
    year: "Pre-Production",
    poster: img("nawabistan_shore_rivalry_closeup_1.png"),
    badges: ["Epic Period Drama", "Pre-Production"],
    highlights: [
      "Epic Period Drama",
      "Dual Nawab Narrative",
      "Historical Grandeur",
      "Global Appeal",
    ],
  },
];

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "all",      label: "All Projects",       icon: BookOpen },
  { id: "current",  label: "Current Production", icon: Zap },
  { id: "past",     label: "Past Achievements",  icon: Star },
  { id: "upcoming", label: "Upcoming Projects",  icon: Film },
  { id: "epic",     label: "Epic Universes",      icon: Globe },
];

// ─── POSTER IMAGE COMPONENT ───────────────────────────────────────────────────
function PosterImage({ src, alt, className = "" }: { src: string; alt: string; className?: string }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div className={`flex items-center justify-center bg-muted/50 text-muted-foreground ${className}`}>
        <Film className="w-12 h-12 opacity-30" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErr(true)}
    />
  );
}

// ─── LIGHTBOX ─────────────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }: { images: string[]; startIndex: number; onClose: () => void }) {
  const [idx, setIdx] = useState(startIndex);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={onClose}>
        <X className="w-8 h-8" />
      </button>
      <button
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
        onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }}
      >
        <ChevronRight className="w-10 h-10 rotate-180" />
      </button>
      <img
        src={images[idx]}
        alt="Gallery"
        className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
        onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }}
      >
        <ChevronRight className="w-10 h-10" />
      </button>
      <div className="absolute bottom-4 text-white/60 text-sm">{idx + 1} / {images.length}</div>
    </motion.div>
  );
}

// ─── SECTION HEADING ──────────────────────────────────────────────────────────
function SectionHeading({ label, title, subtitle }: { label: string; title: string; subtitle: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center mb-16"
    >
      <span className="inline-block text-xs font-bold tracking-[0.25em] text-primary uppercase mb-3 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/5">
        {label}
      </span>
      <h2 className="text-4xl md:text-5xl font-black mb-4">{title}</h2>
      <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{subtitle}</p>
    </motion.div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function Projects() {
  const [activeTab, setActiveTab] = useState("all");
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);
  const [expandedPast, setExpandedPast] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-background to-accent/10" />
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 40px,var(--border) 40px,var(--border) 41px),repeating-linear-gradient(90deg,transparent,transparent 40px,var(--border) 40px,var(--border) 41px)" }} />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-primary text-sm font-bold tracking-[0.25em] uppercase mb-5">
              A Journey Through Cinematic Excellence
            </p>
            <h1 className="text-6xl md:text-8xl font-black mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-none">
              FILMOGRAPHY
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              From groundbreaking debut features to visionary cinematic universes — every frame tells a story.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── STICKY TAB BAR ───────────────────────────────────────────────── */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          TAB 0 — ALL PROJECTS
      ════════════════════════════════════════════════════════════════════ */}
      {activeTab === "all" && (
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-7xl">
            <SectionHeading
              label="Complete Filmography"
              title="All Projects"
              subtitle="Every film, universe, and production — past, present, and future — in one place."
            />

            {/* ── CURRENT IN PRODUCTION ── */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-black tracking-widest uppercase">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  Now In Production
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="rounded-2xl border border-primary/40 bg-card overflow-hidden cursor-pointer group"
                onClick={() => setLightbox({ images: [currentProduction.poster], index: 0 })}
                style={{ boxShadow: "0 0 40px -8px color-mix(in srgb, var(--primary) 20%, transparent)" }}
              >
                <div className="grid md:grid-cols-[240px_1fr]">
                  <div className="relative aspect-[2/3] md:aspect-auto overflow-hidden">
                    <PosterImage src={currentProduction.poster} alt={currentProduction.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/30 hidden md:block" />
                  </div>
                  <div className="p-6 flex flex-col gap-3 justify-center">
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-primary/10 text-primary border border-primary/30 font-bold">{currentProduction.genre}</Badge>
                      {currentProduction.badges.map((b) => <Badge key={b} variant="secondary">{b}</Badge>)}
                    </div>
                    <div>
                      <h3 className="text-2xl font-black mb-1">{currentProduction.title}</h3>
                      <p className="text-primary font-bold">{currentProduction.tagline}</p>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">{currentProduction.description}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {currentProduction.cast.map((a) => <span key={a} className="px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold">{a}</span>)}
                    </div>
                    <Button asChild size="sm" className="self-start gap-2 mt-2">
                      <a href={currentProduction.officialWebsite} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        <ExternalLink className="w-3.5 h-3.5" /> Visit Official Website
                      </a>
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* ── PAST ACHIEVEMENTS ── */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/30 text-sm font-black tracking-widest uppercase">
                  <Award className="w-4 h-4" /> Past Achievements
                </span>
                <div className="flex-1 h-px bg-border" />
                <span className="text-muted-foreground text-sm">{pastProjects.length} Films</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {pastProjects.map((project, i) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all duration-300 cursor-pointer flex flex-col"
                    onClick={() => { const imgs = [project.poster, ...project.galleryImages].filter(Boolean); if (imgs.length) setLightbox({ images: imgs, index: 0 }); }}
                  >
                    <div className="relative aspect-[2/3] overflow-hidden bg-muted/20">
                      <PosterImage src={project.poster} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3">
                        <Badge variant="outline" className="bg-black/50 text-white border-white/20 backdrop-blur-sm text-xs font-bold">{project.year}</Badge>
                      </div>
                      {project.awards && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-amber-500/90 text-white border-0 text-xs gap-1 backdrop-blur-sm"><Award className="w-3 h-3" /></Badge>
                        </div>
                      )}
                      <div className="absolute bottom-3 left-3 right-3">
                        <Badge className="bg-muted/80 text-foreground border-0 text-xs backdrop-blur-sm">{project.genre}</Badge>
                      </div>
                    </div>
                    <div className="p-4 flex flex-col gap-1.5 flex-1">
                      <h3 className="font-black text-base leading-tight group-hover:text-primary transition-colors">{project.title}</h3>
                      <p className="text-xs text-primary font-bold italic">{project.tagline}</p>
                      <p className="text-muted-foreground text-xs leading-relaxed flex-1 line-clamp-3">{project.description}</p>
                      {project.awards && <p className="text-xs font-bold text-amber-600 flex items-center gap-1 mt-1"><Award className="w-3 h-3" />{project.awards}</p>}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* ── UPCOMING ── */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/10 text-amber-500 border border-amber-400/30 text-sm font-black tracking-widest uppercase">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> Pre-Production
                </span>
                <div className="flex-1 h-px bg-border" />
                <span className="text-muted-foreground text-sm">{upcomingProjects.length} Films</span>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {upcomingProjects.map((project, i) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all duration-300 cursor-pointer flex flex-col"
                    onClick={() => { if (project.poster) setLightbox({ images: [project.poster], index: 0 }); }}
                  >
                    <div className="relative aspect-[2/3] overflow-hidden bg-muted/20">
                      <PosterImage src={project.poster} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3">
                        <span className="flex items-center gap-1 text-[9px] font-black tracking-widest uppercase text-amber-400 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
                          <span className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" /> Pre-Prod
                        </span>
                      </div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <Badge className="bg-muted/80 text-foreground border-0 text-[10px] backdrop-blur-sm">{project.genre}</Badge>
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <h3 className="font-black text-sm leading-tight group-hover:text-primary transition-colors">{project.title}</h3>
                      <p className="text-[10px] text-primary font-bold italic">{project.tagline}</p>
                      <p className="text-muted-foreground text-[10px] leading-relaxed line-clamp-2">{project.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* ── EPIC UNIVERSES ── */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary border border-primary/30 text-sm font-black tracking-widest uppercase">
                  <Globe className="w-4 h-4" /> Epic Universes
                </span>
                <div className="flex-1 h-px bg-border" />
                <span className="text-muted-foreground text-sm">{epicUniverses.length} Universes</span>
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                {epicUniverses.map((universe, i) => (
                  <motion.div
                    key={universe.id}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all duration-300 cursor-pointer flex flex-col"
                    onClick={() => setLightbox({ images: [universe.poster], index: 0 })}
                  >
                    <div className="relative aspect-[2/3] overflow-hidden bg-muted/20">
                      <PosterImage src={universe.poster} alt={universe.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-primary/80 text-primary-foreground border-0 text-[10px] backdrop-blur-sm font-black uppercase tracking-wider">Epic Universe</Badge>
                      </div>
                    </div>
                    <div className="p-4 flex flex-col gap-1.5 flex-1">
                      <h3 className="font-black text-sm leading-tight group-hover:text-primary transition-colors">{universe.title}</h3>
                      <p className="text-[10px] text-primary font-bold italic">{universe.tagline}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {universe.badges.slice(0, 2).map((b) => <Badge key={b} variant="secondary" className="text-[10px] px-2 py-0">{b}</Badge>)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB 1 — CURRENT PRODUCTION
      ════════════════════════════════════════════════════════════════════ */}
      {activeTab === "current" && (
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <SectionHeading
              label="Now Filming"
              title="Current Production"
              subtitle="The most ambitious project yet — a global AI geopolitical thriller redefining Indian cinema."
            />

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="rounded-3xl overflow-hidden border border-primary/30 bg-card"
              style={{ boxShadow: "0 0 80px -10px color-mix(in srgb, var(--primary) 25%, transparent)" }}
            >
              {/* Status bar */}
              <div className="bg-primary px-8 py-3 flex items-center justify-between">
                <span className="text-primary-foreground font-black tracking-[0.2em] text-xs uppercase flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  {currentProduction.status}
                </span>
                <span className="text-primary-foreground/80 text-sm font-medium">{currentProduction.year}</span>
              </div>

              <div className="grid lg:grid-cols-2 gap-0">
                {/* Poster */}
                <div className="relative aspect-[2/3] lg:aspect-auto lg:min-h-[600px] overflow-hidden">
                  <PosterImage
                    src={currentProduction.poster}
                    alt={currentProduction.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/50 hidden lg:block" />
                </div>

                {/* Info */}
                <div className="p-8 md:p-12 flex flex-col gap-6 justify-center">
                  {/* Badges */}
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-primary/10 text-primary border border-primary/30 font-bold">{currentProduction.genre}</Badge>
                    {currentProduction.badges.map((b) => (
                      <Badge key={b} variant="secondary">{b}</Badge>
                    ))}
                  </div>

                  <div>
                    <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-2 leading-none">
                      {currentProduction.title}
                    </h2>
                    <p className="text-xl text-primary font-bold">{currentProduction.tagline}</p>
                  </div>

                  <p className="text-muted-foreground leading-relaxed text-base">{currentProduction.description}</p>

                  {/* Cast */}
                  <div>
                    <h4 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3 flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-primary" /> Star Cast
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {currentProduction.cast.map((actor) => (
                        <span key={actor} className="px-4 py-2 rounded-full bg-muted border border-border text-sm font-semibold">
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Highlights */}
                  <div>
                    <h4 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3 flex items-center gap-2">
                      <Star className="w-3.5 h-3.5 text-primary" /> Production Highlights
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {currentProduction.highlights.map((h) => (
                        <div key={h} className="flex items-center gap-2.5 text-sm bg-muted/40 rounded-lg px-3 py-2 border border-border/50">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                          {h}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button asChild size="lg" className="gap-2">
                      <a href={currentProduction.officialWebsite} target="_blank" rel="noopener noreferrer">
                        <Play className="w-4 h-4" />
                        Visit Official Website
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB 2 — PAST ACHIEVEMENTS
      ════════════════════════════════════════════════════════════════════ */}
      {activeTab === "past" && (
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-7xl">
            <SectionHeading
              label="Award-Winning Work"
              title="Past Achievements"
              subtitle="A legacy of internationally recognised films spanning drama, music, and spiritual storytelling."
            />

            <div className="space-y-8">
              {pastProjects.map((project, i) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all duration-300"
                  style={{ boxShadow: "0 4px 24px -8px color-mix(in srgb, var(--primary) 12%, transparent)" }}
                >
                  <div className="grid md:grid-cols-[280px_1fr] gap-0">
                    {/* Poster */}
                    <div
                      className="relative cursor-pointer group"
                      onClick={() => {
                        const imgs = [project.poster, ...project.galleryImages].filter(Boolean);
                        if (imgs.length > 0) setLightbox({ images: imgs, index: 0 });
                      }}
                    >
                      <div className="aspect-[2/3] md:aspect-auto md:h-full min-h-[280px] overflow-hidden bg-muted/30">
                        {project.poster ? (
                          <>
                            <PosterImage
                              src={project.poster}
                              alt={project.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/20 backdrop-blur-sm rounded-full p-3">
                                <Play className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="w-16 h-16 text-muted-foreground/30" />
                          </div>
                        )}
                      </div>

                      {/* Gallery thumbnail strip */}
                      {project.galleryImages.length > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 flex gap-1 p-2 bg-gradient-to-t from-black/60 to-transparent">
                          {project.galleryImages.slice(0, 3).map((gImg, gi) => (
                            <div
                              key={gi}
                              className="flex-1 aspect-square rounded overflow-hidden cursor-pointer border border-white/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLightbox({ images: [project.poster, ...project.galleryImages].filter(Boolean), index: gi + 1 });
                              }}
                            >
                              <PosterImage src={gImg} alt={`${project.title} ${gi + 1}`} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-6 md:p-8 flex flex-col gap-4">
                      {/* Year + Genre badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-black text-base px-3">
                          <Calendar className="w-3.5 h-3.5 mr-1.5" />{project.year}
                        </Badge>
                        <Badge className="bg-primary/10 text-primary border border-primary/20 font-semibold">{project.genre}</Badge>
                        {project.duration && (
                          <Badge variant="secondary" className="gap-1">
                            <Clock className="w-3 h-3" />{project.duration}
                          </Badge>
                        )}
                        {project.awards && (
                          <Badge className="bg-amber-500/10 text-amber-600 border border-amber-500/30 gap-1">
                            <Award className="w-3 h-3" />{project.awards}
                          </Badge>
                        )}
                      </div>

                      <div>
                        <h3 className="text-3xl md:text-4xl font-black leading-tight mb-1">{project.title}</h3>
                        <p className="text-primary font-bold italic text-lg">{project.tagline}</p>
                      </div>

                      <p className="text-muted-foreground leading-relaxed">{project.description}</p>

                      {/* Cast */}
                      {project.cast.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-primary" /> Cast
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {project.cast.map((actor) => (
                              <span key={actor} className="px-3 py-1 rounded-full bg-muted border border-border text-sm font-medium">
                                {actor}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Highlights */}
                      <div>
                        <h4 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                          <Star className="w-3.5 h-3.5 text-primary" /> Highlights
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-1.5">
                          {project.highlights.map((h) => (
                            <div key={h} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                              {h}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* View gallery button */}
                      {(project.poster || project.galleryImages.length > 0) && (
                        <div className="pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => {
                              const imgs = [project.poster, ...project.galleryImages].filter(Boolean);
                              if (imgs.length > 0) setLightbox({ images: imgs, index: 0 });
                            }}
                          >
                            <Film className="w-4 h-4" />
                            View Poster & Gallery ({[project.poster, ...project.galleryImages].filter(Boolean).length} images)
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB 3 — UPCOMING PROJECTS
      ════════════════════════════════════════════════════════════════════ */}
      {activeTab === "upcoming" && (
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-7xl">
            <SectionHeading
              label="In Development"
              title="Upcoming Projects"
              subtitle="An ambitious slate of films across genres — from psychological horror to romantic epics — all in pre-production."
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {upcomingProjects.map((project, i) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="group rounded-2xl border border-border bg-card hover:border-primary/40 transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
                  onClick={() => {
                    if (project.poster) setLightbox({ images: [project.poster], index: 0 });
                  }}
                >
                  {/* Poster */}
                  <div className="relative aspect-[2/3] overflow-hidden bg-muted/20">
                    <PosterImage
                      src={project.poster}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3">
                      <span className="flex items-center gap-1.5 text-[10px] font-black tracking-widest uppercase text-amber-400 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        Pre-Production
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <Badge className="bg-muted/80 text-foreground border-0 text-xs backdrop-blur-sm">{project.genre}</Badge>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4 flex flex-col gap-2 flex-1">
                    <h3 className="font-black text-base leading-tight group-hover:text-primary transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-xs text-primary font-bold italic">{project.tagline}</p>
                    <p className="text-muted-foreground text-xs leading-relaxed flex-1">{project.description}</p>

                    {project.poster && (
                      <div className="flex items-center gap-1 text-xs text-primary font-semibold mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Film className="w-3 h-3" /> View Poster
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB 4 — EPIC UNIVERSES
      ════════════════════════════════════════════════════════════════════ */}
      {activeTab === "epic" && (
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <SectionHeading
              label="Unprecedented Scale"
              title="Epic Universes"
              subtitle="Unprecedented cinematic universes that redefine the boundaries of storytelling."
            />

            <div className="space-y-12">
              {epicUniverses.map((universe, i) => (
                <motion.div
                  key={universe.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="rounded-3xl border border-border bg-card overflow-hidden"
                  style={{ boxShadow: "0 8px 50px -10px color-mix(in srgb, var(--primary) 20%, transparent)" }}
                >
                  {/* Accent bar */}
                  <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-primary" />

                  <div className="grid lg:grid-cols-[380px_1fr] gap-0">
                    {/* Poster */}
                    <div
                      className="relative cursor-pointer group"
                      onClick={() => setLightbox({ images: [universe.poster], index: 0 })}
                    >
                      <div className="aspect-[2/3] lg:aspect-auto lg:h-full min-h-[300px] overflow-hidden">
                        <PosterImage
                          src={universe.poster}
                          alt={universe.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/20 backdrop-blur-sm rounded-full p-4">
                            <Play className="w-7 h-7 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-8 md:p-12 flex flex-col gap-6 justify-center">
                      {/* Badges */}
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-primary/10 text-primary border border-primary/30 font-black text-xs tracking-widest uppercase">
                          Epic Universe
                        </Badge>
                        {universe.badges.map((b) => (
                          <Badge key={b} variant="secondary">{b}</Badge>
                        ))}
                      </div>

                      <div>
                        <h3 className="text-3xl md:text-4xl font-black mb-2 leading-tight">{universe.title}</h3>
                        <p className="text-primary font-bold text-xl">{universe.tagline}</p>
                      </div>

                      <p className="text-muted-foreground leading-relaxed">{universe.description}</p>

                      {/* Highlights grid */}
                      <div>
                        <h4 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3 flex items-center gap-2">
                          <BookOpen className="w-3.5 h-3.5 text-primary" /> Universe Highlights
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {universe.highlights.map((h) => (
                            <div key={h} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border/60 text-sm font-semibold">
                              <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                              {h}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setLightbox({ images: [universe.poster], index: 0 })}
                        >
                          <Film className="w-4 h-4" /> View Poster
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-card border-t border-border">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-5">Be Part of the Next Chapter</h2>
            <p className="text-xl text-muted-foreground mb-8">
              We're always looking for talented collaborators, investors, and partners to bring visionary stories to life.
            </p>
            <Button size="lg" asChild>
              <a href="#/contact">Get in Touch</a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── LIGHTBOX ─────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {lightbox && (
          <Lightbox
            images={lightbox.images}
            startIndex={lightbox.index}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
